//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import ReactTooltip from "react-tooltip";
// teammate-health-avatar imports
import { IContainer, IProps, IState } from "./TeammateHealthAvatar.d";
import { teammateHealthAvatarFragments } from "./TeammateHealthAvatar.gql";
import * as s from "./TeammateHealthAvatar.styles";
// other internals
import { AvatarPerson } from "@components/avatar";
import { niceName } from "@util/event.util";
// util
import { logger } from "@util/logger.util";
import { isFocusTimeCrawledStatusSynced } from "@util/team-health.util";
// import { track, TrackingEvents, TrackingHoverDelay } from '@util/analytics.util';
// state
import { IReduxState } from "@reducers/root.reducer";
import {
  setActiveViewTeamHealth,
  setActiveViewCalendarProfile,
} from "@actions/chrome-extension.actions";
// material-ui
import { CircularProgress } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class TeammateHealthAvatarCmpt extends React.Component<IProps, IState> {
  private toolTipId: string;
  // private hoverTimer: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      hovered: false,
    };

    const { calendarId } = props.calendarFocusTimeScore;
    this.toolTipId = `tooltip_for_${calendarId}`;
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private roundToHours = (minutes: number) => {
    if (minutes > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutes / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutes / 30) / 2;
    }
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onClick = (isCrawled: boolean) => {
    const { calendarId, fastCrawlFinished } = this.props;

    if (!fastCrawlFinished || !isCrawled) {
      return;
    }

    if (!!calendarId) {
      // const trackingMessage = isUser
      //   ? TrackingEvents.TEAM_HEALTH.SUMMARY_GO_TO_OWN_PROFILE
      //   : TrackingEvents.TEAM_HEALTH.SUMMARY_GO_TO_PROFILE;
      // track(trackingMessage, { calendarId, from: 'THSummary' });
      this.props.setActiveViewCalendarProfile(calendarId);
    } else {
      // track(TrackingEvents.TEAM_HEALTH.SUMMARY_GO_TO_DASHBOARD);
      this.props.setActiveViewTeamHealth();
    }
  };

  private onHover = (hovered: boolean) => {
    this.setState({ hovered });
    // if (hovered) {
    //   this.hoverTimer = setTimeout(() => {
    //     track(TrackingEvents.TEAM_HEALTH.HEALTH_AVATAR_HOVER);
    //   }, TrackingHoverDelay);
    // }
    // if (!hovered && this.hoverTimer) {
    //   clearTimeout(this.hoverTimer);
    // }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderTooltipContent = ({
    focusMinutes,
    busyMinutes,
    totalMinutes,
    crawledStatus,
  }: ISchema.ICalendarFocusTimeScore) => {
    if (!this.props.fastCrawlFinished || crawledStatus === ISchema.CrawledStatus.Pending) {
      return <div>Still processing...</div>;
    } else if (crawledStatus === ISchema.CrawledStatus.None) {
      // In practice, we shouldn't see crawledStatus of None. If the crawl is scheduled you should see Pending.
      // If we return None, we don't know about this calendar or there was some other error along the way.
      return <div>No data on this calendar</div>;
    } else if (
      [ISchema.CrawledStatus.NoAccess, ISchema.CrawledStatus.VeryStale].includes(crawledStatus)
    ) {
      return (
        <div>
          Sorry, Clockwise does not
          <br />
          have access to this calendar
        </div>
      );
    } else if (!totalMinutes) {
      return <div style={s.toolTipRow}>Out of office</div>;
    }

    const focusHours = this.roundToHours(focusMinutes);
    const busyHours = this.roundToHours(busyMinutes);

    if (Number.isNaN(focusHours) || Number.isNaN(busyHours)) {
      logger.error("Failed to load calendar insights");
      return <div>Insights are currently unavailable</div>;
    }

    return (
      <div>
        <div style={s.toolTipRow}>
          <div>Week selected in calendar</div>
        </div>
        <div style={s.toolTipRow}>
          <div style={s.focusDot} />
          <div>{focusHours} hr Focus Time</div>
        </div>
        <div style={s.toolTipRow}>
          <div style={s.busyDot} />
          <div>{busyHours} hr Meetings</div>
        </div>
      </div>
    );
  };

  private renderProgress = ({
    focusMinutes,
    busyMinutes,
    totalMinutes,
  }: ISchema.ICalendarFocusTimeScore) => {
    const { fastCrawlFinished, maxMinutes, width } = this.props;
    const thickness = 5;
    if (!fastCrawlFinished) {
      return (
        <CircularProgress
          {...s.loadingTime}
          variant="determinate"
          value={100}
          size={width}
          thickness={thickness}
        />
      );
    }
    const denominator = maxMinutes ? maxMinutes : totalMinutes;
    const focusValue = denominator ? (focusMinutes / denominator) * 100 : 0;
    const busyValue = denominator ? (busyMinutes / denominator) * 100 + focusValue : 0;

    if (!totalMinutes) {
      return <span />;
    }

    return [
      <CircularProgress
        key={1}
        {...s.focusTime}
        variant="determinate"
        value={focusValue}
        size={width}
        thickness={thickness}
      />,
      <CircularProgress
        key={2}
        {...s.busyTime}
        variant="determinate"
        value={busyValue}
        size={width}
        thickness={thickness}
      />,
    ];
  };

  public render() {
    const {
      org,
      calendarFocusTimeScore,
      person,
      width,
      isUser,
      fastCrawlFinished,
      hideName,
    } = this.props;
    const name = isUser ? "You" : niceName(person, calendarFocusTimeScore.calendarId);

    const isCrawled =
      calendarFocusTimeScore?.crawledStatus &&
      isFocusTimeCrawledStatusSynced(calendarFocusTimeScore.crawledStatus);

    let cursor = {};
    if (isCrawled) {
      cursor = {
        cursor: "pointer",
      };
    }

    return (
      <div
        style={{
          ...s.container,
          ...cursor,
        }}
        onClick={() => this.onClick(isCrawled)}
        data-for={this.toolTipId}
        data-tip
      >
        <div style={{ opacity: !fastCrawlFinished ? 0.6 : this.state.hovered ? 0.85 : 1 }}>
          {this.renderProgress(calendarFocusTimeScore)}
          <div style={{ ...s.teammate }}>
            <div style={{ ...s.avatarContainer, width, height: width }}>
              <AvatarPerson
                org={org}
                person={person}
                orgPerson={null}
                size={width - 20}
                backupCalendarId={calendarFocusTimeScore.calendarId}
                parentName={"TeammateHealthAvatar"}
                hideUserBadge
                style={{ fontSize: 13 }}
              />
            </div>
            {hideName ? (
              ""
            ) : (
              <div
                style={{
                  ...s.teammateName,
                  maxWidth: width,
                  fontWeight: isUser ? 600 : 400,
                }}
              >
                {name}
              </div>
            )}
          </div>
        </div>
        <ReactTooltip
          id={this.toolTipId}
          place="bottom"
          effect="solid"
          afterShow={() => this.onHover(true)}
          afterHide={() => this.onHover(false)}
        >
          {this.renderTooltipContent(calendarFocusTimeScore)}
        </ReactTooltip>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const TeamHealthSummaryRedux = connect(mapStateToProps, {
  setActiveViewTeamHealth,
  setActiveViewCalendarProfile,
})(TeammateHealthAvatarCmpt);

//////////////////
// RELAY
//////////////////
export const TeammateHealthAvatar = createFragmentContainer<IContainer>(
  TeamHealthSummaryRedux,
  teammateHealthAvatarFragments,
);
